const toFiniteNumber = (value) => {
  if (value === undefined || value === null || value === "" || value === "N/A") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const nestedPayload = (data = {}) => {
  if (data.EnvironmentalData && typeof data.EnvironmentalData === "object") {
    return data.EnvironmentalData;
  }
  if (data.value && typeof data.value === "object" && !Array.isArray(data.value)) {
    return data.value;
  }
  return {};
};

const TELEMETRY_SKIP_KEYS = new Set(["ranges", "sensors", "ignition"]);
const KNOWN_TELEMETRY_TYPES = new Set([
  "speed",
  "temperature",
  "humidity",
  "vibration",
  "smoke",
  "voltage",
]);

const normalizeSensorType = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase()
    .replace("fuel_level", "fuel")
    .replace("tyre_pressure", "pressure")
    .replace("temprature", "temperature");

const SENSOR_UNITS = {
  temperature: "\u00B0C",
  humidity: "%",
  vibration: "m/s\u00B2",
  smoke: "ppm",
  speed: "km/h",
  voltage: "V",
};

const DEFAULT_SENSOR_RANGES = {
  temperature: { min: 2, max: 8 },
  humidity: { min: 30, max: 70 },
  vibration: { min: 0, max: 1.5 },
  smoke: { min: 0, max: 10 },
  voltage: { min: 11.5, max: 14.5 },
};

const toMeaningfulSensorNumber = (value) => {
  const number = toFiniteNumber(value);
  // GPS/location payloads often default missing sensors to 0 — treat that as absent.
  if (number === null || number === 0) return null;
  return number;
};

export const extractLiveTelemetry = (data = {}) => {
  const nested = nestedPayload(data);

  return {
    speed: toFiniteNumber(
      data.Speed ?? data.speed ?? nested.Speed ?? nested.speed,
    ),
    temperature: toMeaningfulSensorNumber(
      data.Temprature ??
        data.Temperature ??
        data.temperature ??
        nested.Temprature ??
        nested.Temperature ??
        nested.temperature ??
        nested.temp,
    ),
    humidity: toMeaningfulSensorNumber(
      data.Humidity ?? data.humidity ?? nested.Humidity ?? nested.humidity ?? nested.hum,
    ),
    vibration: toMeaningfulSensorNumber(
      data.Vibration ?? data.vibration ?? nested.Vibration ?? nested.vibration,
    ),
    smoke: toMeaningfulSensorNumber(data.Smoke ?? data.smoke ?? nested.Smoke ?? nested.smoke),
    voltage: toMeaningfulSensorNumber(
      data.voltage ??
        data.Voltage ??
        data.batteryVoltage ??
        data.battery_voltage ??
        nested.voltage ??
        nested.Voltage ??
        nested.batteryVoltage ??
        nested.battery_voltage ??
        nested.battery,
    ),
    satellites: toFiniteNumber(
      data.numberOfSatellites ??
        data.UsedSatellites?.GPSSatellitesCount ??
        nested.satellites,
    ),
    ignition: nested.ignition ?? data.ignition ?? null,
  };
};

/** Cap GPS speed changes so the UI ramps instead of snapping (e.g. 1 ↔ 10). */
export const MAX_SPEED_STEP_KMH = 3;

export const smoothVehicleSpeed = (
  previousSpeed,
  incomingSpeed,
  { moving = false, maxStep = MAX_SPEED_STEP_KMH } = {},
) => {
  if (incomingSpeed == null || !Number.isFinite(incomingSpeed)) {
    return previousSpeed ?? null;
  }
  const incoming = Math.max(0, incomingSpeed);
  if (previousSpeed == null || !Number.isFinite(previousSpeed)) {
    return incoming;
  }
  if (incoming <= 0.5 && (moving || previousSpeed >= 2)) {
    return previousSpeed;
  }
  const delta = incoming - previousSpeed;
  const limited =
    previousSpeed + Math.max(-maxStep, Math.min(maxStep, delta));
  return Math.round(limited * 10) / 10;
};

export const mergeLiveTelemetry = (current = {}, next = {}) => {
  const merged = {
    speed: next.speed ?? current.speed ?? null,
    temperature: next.temperature ?? current.temperature ?? null,
    humidity: next.humidity ?? current.humidity ?? null,
    vibration: next.vibration ?? current.vibration ?? null,
    smoke: next.smoke ?? current.smoke ?? null,
    voltage: next.voltage ?? current.voltage ?? null,
    satellites: next.satellites ?? current.satellites ?? null,
    ignition: next.ignition ?? current.ignition ?? null,
    ranges: { ...(current.ranges || {}), ...(next.ranges || {}) },
  };

  return merged;
};

export const appendTelemetrySeries = (series = {}, telemetry = {}, maxPoints = 80) => {
  const nextSeries = { ...series };
  const now = Date.now();

  Object.entries(telemetry).forEach(([key, value]) => {
    if (TELEMETRY_SKIP_KEYS.has(key) || key === "satellites") return;
    const number = toFiniteNumber(value);
    if (number === null) return;

    const points = [...(nextSeries[key] || [])];
    const last = points[points.length - 1];
    if (last && last.value === number && now - (last.timestamp || 0) < 4000) {
      return;
    }

    points.push({
      timestamp: now,
      time: new Date(now).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      value: number,
    });
    nextSeries[key] = points.slice(-maxPoints);
  });

  return nextSeries;
};

export const historyRowsToSeriesPoints = (rows = [], type) => {
  const pointsByTimestamp = new Map();

  rows.forEach((row) => {
    const nested = nestedPayload(row);
    const value = toFiniteNumber(
      row?.value ??
        row?.[type] ??
        nested[type] ??
        row?.Speed ??
        nested.speed ??
        nested.temperature,
    );
    if (value === null) return;

    const recordedAt = row?.recordedAt || row?.createdAt || row?.updatedAt || row?.timestamp;
    const timestamp = new Date(recordedAt || Date.now()).getTime();
    if (!Number.isFinite(timestamp)) return;

    pointsByTimestamp.set(timestamp, {
      timestamp,
      time: new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      value,
    });
  });

  return Array.from(pointsByTimestamp.values()).sort((a, b) => a.timestamp - b.timestamp);
};

export const applyMonitorReadings = (readings = []) => {
  const telemetry = {};
  const ranges = {};
  const sensors = [];

  readings.forEach((reading) => {
    const type = normalizeSensorType(
      reading?.sensorType || reading?.deviceType || reading?.type,
    );
    const nested = nestedPayload(reading);
    const nestedTelemetry = extractLiveTelemetry(reading);

    Object.entries(nestedTelemetry).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        telemetry[key] = value;
      }
    });

    const directValue = toFiniteNumber(reading?.value);
    if (directValue !== null && KNOWN_TELEMETRY_TYPES.has(type)) {
      // Same rule as GPS payloads: 0 means "not reporting" for these sensors.
      telemetry[type] = directValue === 0 ? null : directValue;
    }

    if (KNOWN_TELEMETRY_TYPES.has(type) && (directValue !== null || DEFAULT_SENSOR_RANGES[type])) {
      ranges[type] = {
        min: toFiniteNumber(reading?.minValue) ?? DEFAULT_SENSOR_RANGES[type]?.min,
        max: toFiniteNumber(reading?.maxValue) ?? DEFAULT_SENSOR_RANGES[type]?.max,
        unit: reading?.unit || SENSOR_UNITS[type] || nested.unit || "",
      };
    }

    if (nested.temperature != null && !ranges.temperature) {
      ranges.temperature = {
        ...DEFAULT_SENSOR_RANGES.temperature,
        unit: SENSOR_UNITS.temperature,
      };
    }
    if (nested.humidity != null && !ranges.humidity) {
      ranges.humidity = {
        ...DEFAULT_SENSOR_RANGES.humidity,
        unit: SENSOR_UNITS.humidity,
      };
    }

    sensors.push({
      type,
      trackingDeviceId: reading?.trackingDeviceId,
      deviceType: reading?.deviceType,
      sensorType: reading?.sensorType,
      unit: reading?.unit || SENSOR_UNITS[type] || "",
      min: toFiniteNumber(reading?.minValue),
      max: toFiniteNumber(reading?.maxValue),
      value: directValue,
    });
  });

  return { ...telemetry, ranges, sensors };
};

export const formatSensorValue = (value, unit = "") => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "---";
  }
  const number = Number(value);
  const digits = Number.isInteger(number) ? 0 : 1;
  return `${number.toFixed(digits)}${unit ? ` ${unit}` : ""}`;
};

export const getReadingColor = (value, min, max) => {
  const number = toFiniteNumber(value);
  if (number === null) return "#050C1C";
  if (min !== undefined && min !== null && number < min) return "#EE3B2B";
  if (max !== undefined && max !== null && number >= max) return "#EE3B2B";
  return "#008C23";
};

export { SENSOR_UNITS, DEFAULT_SENSOR_RANGES };
